import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Pill, Plus, Search, Printer, Package, User, AlertTriangle, CheckCircle2, Trash2, FileText } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export default function PharmacyDispensing() {
  const { currentCompany } = useAuth();
  const [dispenses, setDispenses] = useState([]);
  const [products, setProducts] = useState([]);
  const [prescriptions, setPrescriptions] = useState([]);
  const [dlg, setDlg] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [selectedRx, setSelectedRx] = useState(null);

  function emptyForm() { return { prescription_id: null, patient_id: null, patient_name: "", patient_phone: "", doctor_name: "", external_rx_number: "", items: [], notes: "" }; }

  const load = async () => {
    try {
      const [d, p, rx] = await Promise.all([
        api.get("/pharmacy/dispenses"),
        api.get("/products"),
        api.get("/clinic/prescriptions").catch(() => ({ data: [] })),
      ]);
      setDispenses(d.data);
      setProducts(p.data);
      setPrescriptions(rx.data);
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(emptyForm()); setSelectedRx(null); setDlg(true); };

  const loadFromRx = (rx) => {
    // Convert clinic prescription drugs into pharmacy items (match by name if possible)
    const items = (rx.drugs || []).map(d => {
      const p = products.find(prod => (prod.name || "").toLowerCase().includes((d.name || "").toLowerCase()));
      return {
        product_id: p ? p.id : "",
        name: d.name,
        dosage: d.dosage,
        duration: d.duration,
        quantity: 1,
        _search: d.name,
      };
    });
    setForm({
      prescription_id: rx.id,
      patient_id: rx.patient_id, patient_name: rx.patient_name,
      patient_phone: rx.patient_phone || "",
      doctor_name: rx.doctor_name || "",
      external_rx_number: "", items, notes: rx.notes || "",
    });
    setSelectedRx(rx);
    setDlg(true);
  };

  const updItem = (i, patch) => { const it = [...form.items]; it[i] = { ...it[i], ...patch }; setForm({ ...form, items: it }); };
  const rmItem = (i) => setForm({ ...form, items: form.items.filter((_, x) => x !== i) });
  const addItem = () => setForm({ ...form, items: [...form.items, { product_id: "", quantity: 1 }] });

  const save = async () => {
    const clean = form.items.filter(it => it.product_id).map(it => ({ product_id: it.product_id, quantity: parseFloat(it.quantity) || 1 }));
    if (clean.length === 0) return toast.error("اختر دواءً واحداً على الأقل");
    try {
      const { data } = await api.post("/pharmacy/dispenses", { ...form, items: clean });
      toast.success("تم صرف الوصفة");
      if (window.confirm("طباعة إيصال الصرف الآن؟")) print(data.id);
      setDlg(false); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const print = async (did) => {
    try {
      const { data } = await api.get(`/pharmacy/dispenses/${did}`);
      const w = window.open("", "_blank");
      if (!w) return;
      const rows = (data.items || []).map((it, i) => `<tr><td>${i + 1}</td><td>${it.name}</td><td style="text-align:center">${it.quantity}</td><td style="text-align:end">${(it.price || 0).toLocaleString("ar-EG")}</td><td style="text-align:end">${(it.line_total || 0).toLocaleString("ar-EG")}</td></tr>`).join("");
      w.document.write(`<html dir="rtl"><head><title>إيصال صرف وصفة</title>
      <style>body{font-family:sans-serif;padding:20px;max-width:600px;margin:auto}h1{color:#059669;text-align:center;margin:0 0 4px}.info{background:#f0fdf4;padding:8px;border-radius:6px;margin:10px 0;font-size:13px;display:grid;grid-template-columns:1fr 1fr;gap:4px}table{width:100%;border-collapse:collapse;margin:10px 0}th{background:#059669;color:#fff;padding:6px;font-size:12px}td{border-bottom:1px solid #e5e7eb;padding:6px;font-size:12px}.tot{text-align:end;padding:10px;background:#f0fdf4;font-size:16px;font-weight:bold;color:#059669}</style>
      </head><body>
      <h1>💊 إيصال صرف وصفة</h1>
      <div style="text-align:center;color:#64748b;font-size:12px">${data.company?.name || ""} · ${data.company?.phone || ""}</div>
      <div class="info">
        <div><b>المريض:</b> ${data.patient_name || "—"}</div>
        <div><b>الهاتف:</b> ${data.patient_phone || "—"}</div>
        <div><b>الطبيب:</b> ${data.doctor_name || "—"}</div>
        <div><b>التاريخ:</b> ${new Date(data.created_at).toLocaleString("ar-EG")}</div>
        <div><b>الصيدلي:</b> ${data.dispensed_by_name || "—"}</div>
        <div><b>رقم الوصفة:</b> ${data.external_rx_number || data.prescription_id?.slice(0, 8) || "—"}</div>
      </div>
      <table><thead><tr><th>#</th><th>الدواء</th><th>الكمية</th><th>السعر</th><th>المجموع</th></tr></thead><tbody>${rows}</tbody></table>
      <div class="tot">الإجمالي: ${(data.total || 0).toLocaleString("ar-EG")}</div>
      ${data.notes ? `<div style="font-size:11px;color:#64748b;padding:8px;background:#f8fafc;border-radius:4px;margin-top:8px">${data.notes}</div>` : ""}
      </body></html>`);
      w.document.close(); setTimeout(() => w.print(), 500);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const pendingRx = prescriptions.filter(rx => !rx.dispensed_at);

  return (
    <div className="space-y-4" data-testid="dispensing-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-heading font-bold text-2xl flex items-center gap-2"><Pill className="w-6 h-6 text-primary" /> صرف الوصفات</h1>
          <p className="text-sm text-muted-foreground mt-1">صرف الأدوية للمرضى مع سجل كامل مربوط بوصفة الطبيب</p>
        </div>
        <Button onClick={openNew} className="gap-2" data-testid="new-dispense-btn"><Plus className="w-4 h-4" /> صرف وصفة يدوية</Button>
      </div>

      {/* Pending Rx from clinics */}
      {pendingRx.length > 0 && (
        <Card className="border-amber-200 bg-amber-50/50">
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><FileText className="w-5 h-5 text-amber-600" /> وصفات بانتظار الصرف ({pendingRx.length})</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-1.5">
              {pendingRx.slice(0, 8).map(rx => (
                <div key={rx.id} className="flex items-center justify-between p-2 bg-background rounded border" data-testid={`rx-${rx.id}`}>
                  <div className="flex-1">
                    <div className="text-sm font-medium">{rx.patient_name} <span className="text-xs text-muted-foreground">— {rx.drugs?.length || 0} أدوية</span></div>
                    <div className="text-xs text-muted-foreground">{rx.doctor_name && `د. ${rx.doctor_name} · `}{new Date(rx.created_at).toLocaleDateString("ar-EG")}</div>
                  </div>
                  <Button size="sm" onClick={() => loadFromRx(rx)} data-testid={`dispense-rx-${rx.id}`}><Pill className="w-3.5 h-3.5 me-1" /> صرف</Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* History */}
      <Card>
        <CardHeader><CardTitle className="text-base">سجل الصرف ({dispenses.length})</CardTitle></CardHeader>
        <CardContent>
          {dispenses.length === 0 ? (
            <div className="text-center py-8 text-sm text-muted-foreground">لا يوجد صرف بعد</div>
          ) : (
            <div className="space-y-1">
              {dispenses.map(d => (
                <div key={d.id} className="flex items-center justify-between p-2 border rounded hover:bg-muted/30" data-testid={`disp-${d.id}`}>
                  <div className="flex-1">
                    <div className="text-sm font-medium">{d.patient_name || "بدون مريض"} — <span className="text-primary">{(d.total || 0).toLocaleString("ar-EG")}</span></div>
                    <div className="text-xs text-muted-foreground">{d.items?.length || 0} أدوية · {new Date(d.created_at).toLocaleString("ar-EG")} · بواسطة {d.dispensed_by_name}</div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => print(d.id)} className="h-7 text-xs"><Printer className="w-3 h-3 me-1" /> طباعة</Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dispense dialog */}
      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="dispense-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Pill className="w-5 h-5" /> {selectedRx ? "صرف وصفة من العيادة" : "صرف وصفة يدوية"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div><Label>اسم المريض</Label><Input value={form.patient_name} onChange={e => setForm({ ...form, patient_name: e.target.value })} data-testid="pt-name" /></div>
              <div><Label>الهاتف</Label><Input value={form.patient_phone} onChange={e => setForm({ ...form, patient_phone: e.target.value })} /></div>
              <div><Label>اسم الطبيب</Label><Input value={form.doctor_name} onChange={e => setForm({ ...form, doctor_name: e.target.value })} /></div>
              <div><Label>رقم الوصفة الخارجية</Label><Input value={form.external_rx_number} onChange={e => setForm({ ...form, external_rx_number: e.target.value })} placeholder="لو من عيادة خارج النظام" /></div>
            </div>
            <div>
              <Label>الأدوية المطلوب صرفها</Label>
              <div className="space-y-2">
                {form.items.map((it, i) => (
                  <div key={i} className="grid grid-cols-[1fr_100px_auto] gap-2 items-center" data-testid={`disp-item-${i}`}>
                    <select value={it.product_id} onChange={e => updItem(i, { product_id: e.target.value })} className="h-10 px-2 border rounded bg-background text-sm">
                      <option value="">— اختر دواء —{it._search ? ` (${it._search})` : ""}</option>
                      {products.filter(p => p.is_pharma).map(p => (
                        <option key={p.id} value={p.id}>{p.name} {p.stock !== undefined ? `[متوفر: ${p.stock}]` : ""}</option>
                      ))}
                    </select>
                    <Input type="number" value={it.quantity} onChange={e => updItem(i, { quantity: parseFloat(e.target.value) || 1 })} placeholder="الكمية" />
                    <Button size="icon" variant="ghost" onClick={() => rmItem(i)} className="text-red-500 h-9 w-9"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                ))}
              </div>
              <Button size="sm" variant="outline" onClick={addItem} className="mt-2" data-testid="add-item-btn"><Plus className="w-3 h-3 me-1" /> دواء آخر</Button>
            </div>
            <div><Label>ملاحظات</Label><Textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDlg(false)}>إلغاء</Button><Button onClick={save} data-testid="save-dispense-btn"><CheckCircle2 className="w-4 h-4 me-1" /> صرف الوصفة</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
